import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Avatar, 
  Chip, 
  Button,
  Link,
  CircularProgress,
  Divider
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  EmojiEvents, 
  Groups,
  Person,
  Leaderboard as LeaderboardIcon,
  LocalFireDepartment,
  CheckCircle,
  OpenInNew
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import Layout from '../components/Layout';
import { discoverService } from '../services/discoverService';
import { formatRelativeTimeShort } from '../utils/dateUtils';

// Styled components
const PrizeCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary}DD 100%)`,
  borderRadius: '20px',
  padding: theme.spacing(3),
  color: 'white',
  marginBottom: theme.spacing(3),
}));

const StatCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})(({ theme, clickable }) => ({
  padding: theme.spacing(2),
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary}DD 100%)`,
  color: 'white',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': clickable ? {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${colors.primary}40`,
  } : {},
}));

const LeaderboardItem = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== 'isCurrentUser' && prop !== 'isTopThree' && prop !== 'isHighlighted',
})(({ theme, isCurrentUser, isTopThree, isHighlighted }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isHighlighted ? `${colors.primary}20` : isCurrentUser ? `${colors.primary}08` : 'white',
  border: isHighlighted ? `2px solid ${colors.primary}` : isTopThree ? `1px solid ${colors.primary}30` : 'none',
  borderRadius: '12px',
  transition: 'transform 0.1s ease, background-color 0.3s ease, border 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateX(4px)',
  },
}));

const FeedItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  backgroundColor: 'white',
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: 'white',
  borderRadius: '100px',
  padding: '4px',
  position: 'relative',
  marginBottom: theme.spacing(3),
}));

const SliderIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeTab',
})(({ activeTab }) => ({
  position: 'absolute',
  width: 'calc(50% - 2px)',
  height: 'calc(100% - 8px)',
  backgroundColor: `${colors.primary}15`,
  borderRadius: '100px',
  top: '4px',
  left: '4px',
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: activeTab === 0 ? 'translateX(0)' : 'translateX(calc(100% + 4px))',
}));

const SliderOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(2, 2),
  borderRadius: '100px',
  cursor: 'pointer',
  zIndex: 1,
  transition: 'color 0.2s ease',
  color: active ? colors.primary : colors.text?.secondary || '#666',
  fontWeight: 700,
  fontSize: '1rem',
  '&:hover': {
    color: colors.primary,
  },
}));

const ContentSliderWrapper = styled(Box)(({ theme }) => ({
  // Extend beyond paper padding for full-width content
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  overflow: 'hidden',
}));

const ContentSlider = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeTab',
})(({ activeTab }) => ({
  display: 'flex',
  width: '200%',
  transform: `translateX(${activeTab === 0 ? '0' : '-50%'})`,
  transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ContentPane = styled(Box)(({ theme }) => ({
  width: '50%',
  flexShrink: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

// Get current month name
const getCurrentMonth = () => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[new Date().getMonth()];
};

export default function Discover() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasEnteredContest, setHasEnteredContest] = useState(false);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [feed, setFeed] = useState([]);
  const [userStats, setUserStats] = useState({ streak: 0, rank: 0, score: 0 });
  const [highlightUserRank, setHighlightUserRank] = useState(false);
  const [claimInfo, setClaimInfo] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const leaderboardRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Handle touch swipe gestures (for content area and slider)
  const createSwipeHandlers = () => ({
    onTouchStart: (e) => {
      touchStartX.current = e.touches[0].clientX;
    },
    onTouchMove: (e) => {
      touchEndX.current = e.touches[0].clientX;
    },
    onTouchEnd: () => {
      const swipeThreshold = 50;
      const diff = touchStartX.current - touchEndX.current;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0 && activeTab === 0) {
          // Swiped left - go to leaderboard
          setActiveTab(1);
        } else if (diff < 0 && activeTab === 1) {
          // Swiped right - go to community
          setActiveTab(0);
        }
      }
      
      // Reset
      touchStartX.current = 0;
      touchEndX.current = 0;
    }
  });

  const swipeHandlers = createSwipeHandlers();

  const handleRankCardClick = () => {
    // Switch to leaderboard tab
    if (activeTab !== 1) {
      setActiveTab(1);
    }
    
    // Highlight user's rank
    setHighlightUserRank(true);
    
    // Scroll to the current user's leaderboard entry (fallback to top of leaderboard)
    setTimeout(() => {
      const el = document.getElementById('current-user-leaderboard-entry');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        leaderboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Remove highlight after 2 seconds
      setTimeout(() => {
        setHighlightUserRank(false);
      }, 2000);
    }, 200);
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch all data in parallel, but handle each result independently
      const results = await Promise.allSettled([
        discoverService.getLeaderboard(),
        discoverService.getFeed(),
        discoverService.getContestStatus(),
      ]);
      
      const [leaderboardResult, feedResult, contestStatusResult] = results;
      
      // Process leaderboard data
      if (leaderboardResult.status === 'fulfilled' && leaderboardResult.value) {
        const leaderboardData = leaderboardResult.value;
        if (leaderboardData.entries) {
          setLeaderboard(leaderboardData.entries);
        }
        const cu = leaderboardData.currentUser;
        if (cu) {
          setUserStats({
            streak: cu.streak || 0,
            rank: cu.rank || 0,
            score: cu.score || 0
          });
          // Only set disqualified from leaderboard if contestStatus doesn't have it
          if (cu.isDisqualified !== undefined) {
            setIsDisqualified(!!cu.isDisqualified);
          }
        } else {
          const currentUser = leaderboardData.entries?.find(e => e.isCurrentUser);
          if (currentUser) {
            setUserStats({ 
              streak: currentUser.streak, 
              rank: currentUser.rank,
              score: currentUser.score 
            });
          }
        }
      } else if (leaderboardResult.status === 'rejected') {
        console.error('Error fetching leaderboard:', leaderboardResult.reason);
      }
      
      // Process feed data
      if (feedResult.status === 'fulfilled' && feedResult.value?.events) {
        setFeed(feedResult.value.events);
      } else if (feedResult.status === 'rejected') {
        console.error('Error fetching feed:', feedResult.reason);
      }
      
      // Process contest status - this takes precedence for enrollment/disqualification
      if (contestStatusResult.status === 'fulfilled' && contestStatusResult.value) {
        const contestStatus = contestStatusResult.value;
        if (contestStatus.enrollment) {
          setHasEnteredContest(!!contestStatus.enrollment.isEnrolled);
          setIsDisqualified(!!contestStatus.enrollment.isDisqualified);
        } else {
          // Fallback for older API response format
          setHasEnteredContest(!!contestStatus.hasEntered);
          // Keep isDisqualified from leaderboard if enrollment object is missing
        }
        // Process claim info for winners
        if (contestStatus.claim) {
          setClaimInfo(contestStatus.claim);
        } else {
          setClaimInfo(null);
        }
      } else if (contestStatusResult.status === 'rejected') {
        console.error('Error fetching contest status:', contestStatusResult.reason);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEnterContest = async () => {
    try {
      window.location.href = '/account';
    } catch (error) {
      console.error('Error entering contest:', error);
    }
  };

  const handleClaimPrize = async () => {
    if (!claimInfo?.canClaim) return;
    
    setClaimLoading(true);
    try {
      const result = await discoverService.claimPrize();
      if (result.success && result.data?.onboardingUrl) {
        // Open Stripe onboarding in a new window
        window.open(result.data.onboardingUrl, '_blank');
        // Refresh data when the user returns to this tab
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            fetchData();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } else {
        alert(result.message || 'Unable to claim prize. Please try again.');
      }
    } catch (error) {
      console.error('Error claiming prize:', error);
      alert(error.response?.data?.message || 'Unable to claim prize. Please try again later.');
    } finally {
      setClaimLoading(false);
    }
  };

  const getClaimStatusText = (status) => {
    const statusTexts = {
      pending_claim: 'Ready to Claim',
      onboarding: 'Verify Your Account',
      ready_to_pay: 'Verification Complete',
      payout_triggered: 'Payout Processing',
      paid: 'Prize Paid!',
      failed: 'Payout Failed',
    };
    return statusTexts[status] || 'Unknown';
  };

  const getClaimStatusColor = (status) => {
    if (status === 'paid') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'ready_to_pay' || status === 'payout_triggered') return 'warning';
    return 'primary';
  };

  const renderPrizeCard = () => (
    <PrizeCard elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 48, height: 48 }}>
          <EmojiEvents />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5 }}>
            {getCurrentMonth()} Challenge
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Monthly Budgeting Challenge
          </Typography>
        </Box>
      </Box>
      
      {/* Winner Claim Section */}
      {claimInfo?.hasClaimablePrize && (
        <Box 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.15)', 
            borderRadius: 2, 
            p: 2, 
            mb: 2,
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          <Typography variant="h6" fontWeight="700" sx={{ mb: 1 }}>
            🎉 You Won {claimInfo.contestPeriodDisplay}!
          </Typography>
          <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
            ${claimInfo.prizeAmount} Prize
          </Typography>
          <Chip
            label={getClaimStatusText(claimInfo.claimStatus)}
            color={getClaimStatusColor(claimInfo.claimStatus)}
            size="small"
            sx={{ mb: 2 }}
          />
          {claimInfo.canClaim && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleClaimPrize}
              disabled={claimLoading}
              sx={{
                bgcolor: colors.status.ready,
                color: 'white',
                fontWeight: 700,
                borderRadius: '12px',
                py: 1.5,
                '&:hover': { bgcolor: `${colors.status.ready}DD` }
              }}
            >
              {claimLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                claimInfo.claimStatus === 'onboarding' ? 'Continue Verification' : 'Claim Your Prize'
              )}
            </Button>
          )}
          {claimInfo.claimStatus === 'ready_to_pay' && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, textAlign: 'center' }}>
              Verification complete! Admin will process your payout soon.
            </Typography>
          )}
          {claimInfo.claimStatus === 'payout_triggered' && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, textAlign: 'center' }}>
              Your payout is being processed. Expect funds in 1-4 business days.
            </Typography>
          )}
          {claimInfo.claimStatus === 'paid' && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, textAlign: 'center' }}>
              Congratulations! Your prize has been sent to your bank account.
            </Typography>
          )}
        </Box>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            1st Place Prize
          </Typography>
          <Typography variant="h3" fontWeight="700">
            Up to $100 if offered
          </Typography>
        </Box>
        <Box>
            {isDisqualified ? (
              <Chip
                label="Disqualified"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 700,
                }}
              />
            ) : hasEnteredContest ? (
            <Chip
              icon={<CheckCircle sx={{ color: `${colors.status.ready} !important` }} />}
                label="Enrolled"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          ) : (
            <Button
              variant="contained"
              onClick={handleEnterContest}
              sx={{
                bgcolor: 'white',
                color: colors.primary,
                fontWeight: 700,
                borderRadius: '50px',
                px: 3,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
                Manage
            </Button>
          )}
        </Box>
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 1.5 }} />
      
      <Link
        href="https://budgit.app/contest-rules"
        target="_blank"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.85)',
          textDecoration: 'none',
          fontSize: '0.875rem',
          '&:hover': { color: 'white' }
        }}
      >
        View Rules <OpenInNew sx={{ fontSize: 16, ml: 0.5 }} />
      </Link>
    </PrizeCard>
  );

  const renderUserStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6}>
        <StatCard elevation={0} clickable onClick={handleRankCardClick}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255,255,255,0.25)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 1
            }}>
              <LeaderboardIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', flex: 1 }}>
              Your Rank
            </Typography>
            <TrendingUp sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
          </Box>
          <Typography variant="h4" fontWeight="700" sx={{ color: 'white' }}>
            {userStats.rank > 0 ? `#${userStats.rank}` : '—'}
          </Typography>
        </StatCard>
      </Grid>
      <Grid item xs={6}>
        <StatCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255,255,255,0.25)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 1
            }}>
              <LocalFireDepartment sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', flex: 1 }}>
              Streak
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h4" fontWeight="700" sx={{ color: 'white' }}>
              {userStats.streak}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              days
            </Typography>
          </Box>
        </StatCard>
      </Grid>
    </Grid>
  );

  const renderSliderToggle = () => (
    <SliderContainer {...swipeHandlers}>
      <SliderIndicator activeTab={activeTab} />
      <SliderOption active={activeTab === 0} onClick={() => setActiveTab(0)}>
        <Groups sx={{ fontSize: 22 }} />
        <Typography variant="body2" fontWeight="inherit">Community</Typography>
      </SliderOption>
      <SliderOption active={activeTab === 1} onClick={() => setActiveTab(1)}>
        <LeaderboardIcon sx={{ fontSize: 20 }} />
        <Typography variant="body2" fontWeight="inherit">Leaderboard</Typography>
      </SliderOption>
    </SliderContainer>
  );

  const renderLeaderboardItem = (entry) => (
    <LeaderboardItem 
      key={entry.id} 
      id={entry.isCurrentUser ? 'current-user-leaderboard-entry' : undefined}
      elevation={0}
      isCurrentUser={entry.isCurrentUser}
      isTopThree={entry.rank <= 3}
      isHighlighted={entry.isCurrentUser && highlightUserRank}
    >
      <Box sx={{ width: 40, display: 'flex', justifyContent: 'center', mr: 2 }}>
        {entry.rank <= 3 ? (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: entry.rank === 1 ? '#FFD700' : entry.rank === 2 ? '#C0C0C0' : '#CD7F32',
              fontSize: '0.875rem',
              fontWeight: 700,
            }}
          >
            {entry.rank}
          </Avatar>
        ) : (
          <Typography variant="body1" color="text.secondary" fontWeight="600">
            {entry.rank}
          </Typography>
        )}
      </Box>
      
      <Avatar
        src={entry.avatar}
        sx={{
          bgcolor: entry.isCurrentUser ? colors.primary : `${colors.primary}20`,
          color: entry.isCurrentUser ? 'white' : colors.primary,
          mr: 2,
        }}
      >
        {!entry.avatar && <Person />}
      </Avatar>
      
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="subtitle1" 
          fontWeight="600"
          color={entry.isCurrentUser ? 'primary' : 'text.primary'}
        >
          {entry.isCurrentUser ? 'You' : entry.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {entry.streak} day{entry.streak === 1 ? '' : 's'} under budget
        </Typography>
      </Box>
      
      <Box sx={{ textAlign: 'right' }}>
        <Typography 
          variant="h6" 
          fontWeight="700"
          color={entry.isCurrentUser ? 'primary' : 'text.primary'}
        >
          {entry.score}
        </Typography>
        {entry.change !== 0 && (
          <Chip
            size="small"
            icon={entry.change > 0 ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
            label={Math.abs(entry.change)}
            sx={{
              height: 20,
              fontSize: '0.75rem',
              bgcolor: entry.change > 0 ? `${colors.status.ready}20` : `${colors.status.error}20`,
              color: entry.change > 0 ? colors.status.ready : colors.status.error,
              '& .MuiChip-icon': { 
                color: entry.change > 0 ? colors.status.ready : colors.status.error,
                marginLeft: '4px'
              }
            }}
          />
        )}
      </Box>
    </LeaderboardItem>
  );

  const renderFeedItem = (item) => (
    <FeedItem key={item.id} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Avatar
          src={item.userAvatar}
          sx={{
            bgcolor: `${colors.primary}20`,
            color: colors.primary,
            mr: 2,
          }}
        >
          {!item.userAvatar && <Person />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mr: 1 }}>
              {item.userName}
            </Typography>
            {item.badge && (
              <Typography sx={{ fontSize: 20 }}>{item.badge}</Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTimeShort(item.timestamp)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mt: 1.5, pl: 7 }}>
        <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }}>
          {item.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {item.description}
        </Typography>
      </Box>
    </FeedItem>
  );

  const renderEmptyState = (type) => (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      {type === 'feed' ? (
        <Groups sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      ) : (
        <LeaderboardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      )}
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
        {type === 'feed' ? 'No activity yet' : 'No rankings yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {type === 'feed' 
          ? 'Community achievements will appear here'
          : 'Start budgeting to see your rank!'}
      </Typography>
    </Box>
  );

  const renderLoadingState = () => (
    <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Left Column - Feed */}
          <Grid item xs={12} md={8}>
            {renderPrizeCard()}
            
            <Paper sx={{ borderRadius: '20px', overflow: 'hidden', p: 2, pb: 0 }}>
              {renderSliderToggle()}
              
              {loading ? (
                renderLoadingState()
              ) : (
                <ContentSliderWrapper {...swipeHandlers}>
                  <ContentSlider activeTab={activeTab}>
                    {/* Community Tab Content */}
                    <ContentPane>
                      <Typography variant="h6" fontWeight="700" sx={{ mb: 0.5 }}>
                        Community Activity
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        See what others are achieving
                      </Typography>
                      {feed.length > 0 ? (
                        feed.map(renderFeedItem)
                      ) : (
                        renderEmptyState('feed')
                      )}
                    </ContentPane>
                    
                    {/* Leaderboard Tab Content */}
                    <ContentPane>
                      <Box ref={leaderboardRef}>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 0.5 }}>
                          {getCurrentMonth()} Leaderboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Top budgeters this month
                        </Typography>
                        {leaderboard.length > 0 ? (
                          leaderboard.map(renderLeaderboardItem)
                        ) : (
                          renderEmptyState('leaderboard')
                        )}
                      </Box>
                    </ContentPane>
                  </ContentSlider>
                </ContentSliderWrapper>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Quick Stats */}
          <Grid item xs={12} md={4}>
            {renderUserStats()}
            <Paper sx={{ p: 3, borderRadius: '20px', mb: 3 }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2 }}>
                How Scoring Works
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your rank is your period XP for this month — the XP Budgit's servers award you during the contest period.
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Rank is period XP. Staying under budget can earn XP; it is not the rank rule by itself.
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Period XP also includes other in-app actions that award XP, as shown in the app.
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Ties go to current streak, then earlier Official Rules acceptance, then a stable user-id order.
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                The leaderboard updates as XP is awarded or revoked during the period.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Editing an approved budget during its active period disqualifies you for that period.
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 3, borderRadius: '20px' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2 }}>
                Prize Details
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">1st Place</Typography>
                <Typography variant="body2" fontWeight="600">Up to $100 if offered</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Offer only. Award is not automatic. Official rules apply.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
}
